import { cn, getShapeClasses } from '../../utils'
import { forwardRef, type ForwardedRef } from 'react'
import { Icon } from '../Icon'
import { Loader } from '../Loader'
import { Text } from '../Text'
import {
    getBaseClasses,
    getLabelStyle,
    getSizeClasses,
    getVariantClasses,
} from './Button.styles'
import { ButtonProps } from './Button.types'

/**
 * @param to - If provided, renders as an anchor tag
 * @param variant - Default: "contained" - "contained" | "outlined" | "text" | "square" | "transparent" | "fill" | "stroke" | "link" | "rounded"
 * @param size - Default: "md" - "sm" | "base" |  "md" | "lg
 * @param color - Default: "primary" - "primary" | "success" | "warning" | "error"
 * @param isLoading - If true, renders loading spinner
 * @param loaderText - Text to display when isLoading is true
 * @param children - If provided, renders children instead of label
 * @param onClick - If provided, renders as a button
 * @param className - If provided, adds classes to button
 * @param disabled - If true, disables button
 * @param type - Default: "button"
 *
 * @example
 * <Button
 *      label='Button'
 *      variant='contained'
 *      isLoading
 *      disabled
 *      color='primary'
 *      loaderText='Loading'
 *      size='md'
 *      className='w-4 h-4'
 *      type='button'
 *      onClick={handleClick()}
 * />
 *
 * @type
 * ButtonProps{
 *   to?: string
 *   label: string
 *   disabled?: boolean
 *   className?: string
 *   loaderText?: string
 *   isLoading?: boolean
 *   size?: ButtonSize
 *   type?: ButtonType
 *   color?: ColorType
 *   target?: ButtonTarget
 *   variant?: ButtonVariant
 *   children?: React.ReactNode
 *   onClick?: React.MouseEventHandler<HTMLButtonElement | HTMLAnchorElement>
 * }
 */

export const Button = forwardRef<
    HTMLButtonElement | HTMLAnchorElement,
    ButtonProps
>(
    (
        {
            to,
            label,
            disabled,
            children,
            className,
            isLoading,
            iconAfter,
            iconBefore,
            size = 'md',
            type = 'button',
            target = '_self',
            shape = 'rounded',
            color = 'primary',
            variant = 'contained',
            loaderText = 'Učitavanje',
            onClick,
            ...commonProps
        },
        ref
    ) => {
        const classes = cn(
            getBaseClasses(),
            getSizeClasses(size, shape, label),
            getVariantClasses(variant, color, disabled),
            getShapeClasses(shape),
            disabled
                ? 'hover:bg-background-none bg-[hsl(var(--background-neutral))] border-[hsl(var(--background-neutral))] hover:border-[hsl(var(--background-neutral))] text-darkBlue50'
                : '',
            isLoading
                ? 'bg-background-primary/40 fill-background-base rounded-[30px] border border-background-primary/40 justify-center items-center gap-2.5 inline-flex text-background-primary/40 hover:bg-background-none hover:border-background-primary/40 focus:border-background-primary/40'
                : ''
        )

        if (to)
            return (
                <a
                    href={to}
                    className={cn(classes, className)}
                    target={target}
                    ref={ref as ForwardedRef<HTMLAnchorElement>}
                    {...commonProps}
                >
                    {children}
                </a>
            )

        return (
            <button
                type={type}
                onClick={onClick}
                className={cn(
                    classes,
                    className,
                    isLoading && 'cursor-not-allowed py-2'
                )}
                ref={ref as ForwardedRef<HTMLButtonElement>}
                disabled={disabled || isLoading}
                {...commonProps}
            >
                {isLoading ? (
                    <Loader
                        size={size}
                        variant={variant}
                        color="primary"
                        text={loaderText}
                    />
                ) : (
                    <div className="flex items-center whitespace-nowrap">
                        {iconBefore && (
                            <Icon
                                className=""
                                variant={variant}
                                color={color}
                                size={size}
                            >
                                {iconBefore}
                            </Icon>
                        )}
                        <Text
                            className={cn(
                                getLabelStyle(color, disabled),
                                disabled && 'text-darkBlue50'
                            )}
                            text={label}
                            color={color}
                            variant={variant}
                            size={size}
                        />
                        {iconAfter && (
                            <Icon
                                className=""
                                variant={variant}
                                color={color}
                                size={size}
                            >
                                {iconAfter}
                            </Icon>
                        )}
                    </div>
                )}
            </button>
        )
    }
)

Button.displayName = 'Button'
